import styles from './ListingCard.module.css';

import type { FC, ReactNode } from 'react';

import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { CarSimple } from '@phosphor-icons/react';

/**
 * The one listing card in the product — a contract, not a variant.
 *
 * Sale, rental and the dealer page all render this component and differ only in
 * what they put in the slots: the frame ratio, the price position and the status
 * placement stay identical. Anything a vertical needs that is not a slot here is a
 * change to this file, not a fork of it.
 *
 * Under the photo the body is four rows in a fixed order and a fixed rhythm — name, price,
 * second line, status — and every row is there even when its slot is empty (decision №33,
 * 28.09): a card's height does not depend on which slots a vertical fills, so a sale and a
 * rental side by side are the same size and their prices sit on the same line. One thing does
 * make a card taller: a second line too long for the card wraps instead of being cut (see
 * `secondaryLine`), and the cards of its grid row grow by that line together — the name and the
 * price above it stay where they were.
 *
 * The slots are filled by `src/utils/listingCardSlots.ts` (one function per vertical)
 * and the cards sit in `ListingGrid`; this component deliberately has no loading,
 * empty or error state of its own.
 */
export interface ListingCardProps {
  /** Photo. Rendered in a 3:2 frame, cropped low so the car keeps its ground. */
  media?: ReactNode;
  /** Vehicle name — up to two lines, then an ellipsis; the row is two lines tall either way. */
  title: ReactNode;
  /** A label and a number: "Price: 28,000 USD", "Per day: from 60 USD". Its own row, under the name. */
  price?: ReactNode;
  /**
   * Mileage, gearbox and fuel for a sale; deposit · city for a rental. One line when it fits the
   * card — it is not cut when it does not: a long line wraps, and the cards of its grid row grow
   * by that line together.
   */
  secondaryLine?: ReactNode;
  /** Availability, "sold", a next-booking date. Text and border, never a fill. */
  status?: ReactNode;
  /** At most one action. A second lime button on a card cancels the first out. */
  action?: ReactNode;
  /**
   * Where the card leads. A card that leads somewhere IS a link: the whole card is the `<a>`, so
   * it has a name, an address, one Tab stop, a focus ring and a tap target the size of the card
   * (app#231, SF-J2, SF-I3). A destination is the only way to make a card clickable — the same
   * rule `ListItem` follows — so "clickable but unreachable" cannot be written.
   */
  to?: string;
  className?: string;
}

export const ListingCard: FC<ListingCardProps> = ({
  media,
  title,
  price,
  secondaryLine,
  status,
  action,
  to,
  className,
}) => {
  const navigate = useNavigate();

  /**
   * The card is an `<a href>`, so the browser's own link behaviour stays: a middle click or a
   * Cmd/Ctrl click opens the car in a new tab, the address shows on hover, Enter activates it.
   * A plain left click is ours to keep client-side — the same navigation the feed always did.
   */
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!to) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    navigate(to);
  };

  return (
    <Card
      as={to ? 'a' : 'div'}
      href={to}
      className={classNames(styles.listingCard, to && styles.clickable, className)}
      onClick={to ? handleClick : undefined}
      $clickable={!!to}
    >
      {media ? <Media>{media}</Media> : null}
      <Body>
        {/* Four rows, always all four and always in this order — an empty slot keeps its row. */}
        <Title>{title}</Title>
        <Price>{price}</Price>
        <SecondaryLine>{secondaryLine}</SecondaryLine>
        <Status>{status ? <StatusBadge>{status}</StatusBadge> : null}</Status>
        {action ? <Action>{action}</Action> : null}
      </Body>
    </Card>
  );
};

/** The card's photo: one, the first — the gallery lives on the listing — loaded as it nears the screen. */
export const ListingCardPhoto: FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <img src={src} alt={alt} loading="lazy" />
);

/** A car with no photo yet: the frame says so to a screen reader and shows a car outline to everyone else. */
export const ListingCardNoPhoto: FC<{ label: string }> = ({ label }) => (
  <NoPhoto role="img" aria-label={label}>
    <CarSimple aria-hidden />
  </NoPhoto>
);

/**
 * The number of the price with its currency code — "28,000 USD", "from 60 USD". Its own element so
 * the words around it stay out of it, and marked `amount`: the class is how the e2e suite reads the
 * amounts on the feed (SF-F4: none of them may start with 0 or a minus).
 */
export const ListingCardAmount: FC<{ children: ReactNode }> = ({ children }) => (
  <span className={styles.amount}>{children}</span>
);

/** The label before the number ("Price:", "Per day:"): text, so Public Sans — only the number takes the display face. */
export const ListingCardPriceLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.medium.fontSize}px;
  line-height: ${({ theme }) => theme.typography.medium.lineHeight}px;
  font-weight: ${({ theme }) => theme.typography.medium.fontWeight};
  color: ${({ theme }) => theme.colors.mutedWhite};
`;

/* As tall as its row: every card of a grid row is the height of the row, whatever it holds. */
const Card = styled.div<{ $clickable: boolean }>`
  height: 100%;
  background: ${({ theme }) => theme.colors.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small}px;
  overflow: hidden;
  min-width: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background: ${({ theme, $clickable }) =>
      $clickable ? theme.colors.surfaceHover : theme.colors.surfaceRaised};
  }
`;

const Media = styled.div`
  aspect-ratio: 3 / 2;
  /* Without it the frame is only 3:2 for a landscape photo: a box with an aspect ratio may not be
     shorter than its content, so a portrait photo stretched the frame to its own height. */
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceOverlay};

  & > img,
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Cars sit low in a photograph; centring the crop cuts the wheels off. */
    object-position: 50% 58%;
    display: block;
  }
`;

const NoPhoto = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.mutedWhite};

  & svg {
    width: ${({ theme }) => theme.space.xxl}px;
    height: ${({ theme }) => theme.space.xxl}px;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs}px;
  padding: ${({ theme }) => theme.space.sm}px;
`;

/* Two lines, then an ellipsis — and two lines tall even for a short name, so the rows under it
   start at the same height on every card. One line cut mid-name is what SF-I1 calls hidden text;
   a name that wraps is read in full, and a single unbroken word breaks rather than running out. */
const Title = styled.div`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.heading.fontSize}px;
  line-height: ${({ theme }) => theme.typography.heading.lineHeight}px;
  font-weight: ${({ theme }) => theme.typography.heading.fontWeight};
  color: ${({ theme }) => theme.colors.white};
  min-height: ${({ theme }) => theme.typography.heading.lineHeight * 2}px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

/* Its own row under the name, on the left, in one line: the label is text, the number is
   Big Shoulders like every number that carries data. */
const Price = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.typography.title1.fontSize}px;
  line-height: ${({ theme }) => theme.typography.title1.lineHeight}px;
  font-weight: ${({ theme }) => theme.typography.title1.fontWeight};
  color: ${({ theme }) => theme.colors.white};
  min-height: ${({ theme }) => theme.typography.title1.lineHeight}px;
  white-space: nowrap;
`;

const SecondaryLine = styled.div`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.medium.fontSize}px;
  line-height: ${({ theme }) => theme.typography.medium.lineHeight}px;
  color: ${({ theme }) => theme.colors.lightWhite};
  min-height: ${({ theme }) => theme.typography.medium.lineHeight}px;
`;

const Status = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: ${({ theme }) => theme.typography.medium.lineHeight}px;
`;

/* Text and border in the accent, never a fill. The line is two pixels shorter than the caption's
   so that, with the 1px border above and below, the badge is exactly one caption line tall and a
   busy car's card is no taller than a free one's. */
const StatusBadge = styled.span`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.medium.fontSize}px;
  line-height: ${({ theme }) => theme.typography.medium.lineHeight - 2}px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: ${({ theme }) => theme.borderRadius.round}px;
  padding: 0 ${({ theme }) => theme.space.xs}px;
`;

const Action = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.space.xxs}px;
`;
